// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { store } from '@/store'
import moment from 'moment'

// Used for date parsing
const knownDateFormats = [
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DD',
  'YYYY-MM-DDTHH:mm:ss.SSS',
  'YYYY-MM-DDTHH:mm:ss.SSSZ',
  'DD-MMM-YYYY',
]

export const mergeByMrn = (a1, a2) =>
  a1.map((itm) => ({
    ...a2.find((item) => item.MRN.includes(itm.mrn) && item),
    ...itm,
  }))

export const mergeByPhysicianId = (a1, a2) =>
  a1.map((itm) => ({
    ...a2.find((item) => item.care_giver_id === itm.care_giver_id && item),
    ...itm,
  }))

export const calculateAge = (birthday) => {
  const ageDate = calculateDiffDate(birthday, Date())
  return Math.abs(ageDate.getUTCFullYear() - 1970)
}

export const calculateDiffDate = (date1, date2) => {
  const dateDiffMillis = new Date(date2).getTime() - new Date(date1).getTime()
  return new Date(dateDiffMillis)
}

export const showToast = (response, message) => {
  if (response === 'error') {
    store.dispatch('showSystemToast', message || `Error status=${response.status}`)
    store.dispatch('showToastType', 'error')
    setTimeout(() => { store.dispatch('showSystemToast', ''), store.dispatch('showToastType', '') }, 5000)
    return
  }

  else if (response === 'warning') {
    store.dispatch('showSystemToast', message || `warning status=${response.status}`)
    store.dispatch('showToastType', 'warning')
    setTimeout(() => { store.dispatch('showSystemToast', ''), store.dispatch('showToastType', '') }, 5000)
    return
  }

  else if (response === 'success') {
    store.dispatch('showSystemToast', message || `Success status=${response.status}`)
    store.dispatch('showToastType', 'success')
    setTimeout(() => { store.dispatch('showSystemToast', ''), store.dispatch('showToastType', '') }, 5000)
    return
  }

  // show default info toast
  store.dispatch('showSystemToast', message || `Success status=${response.status}`)
  setTimeout(() => { store.dispatch('showSystemToast', '') }, 5000)
  return
}

export const convertToDateTime = (date, time) => {
  // construct a moment object from the date and time. Less code/more readable
  // than using vanilla JS Date object
  const dateTime = moment(`${date} ${time}`, 'DD-MMM-YYYY HH:mm')
  const formatted = dateTime.isValid() ? dateTime.format('ddd MMM DD YYYY HH:mm:ss') : 'Invalid DateTime'

  // need to then convert back to a JS Date for PrimeVue's Calendar component
  return new Date(formatted)
}

export const formatDate = (dateInput) => {
  if (!dateInput) { return '—' }

  const date = parseDateInput(dateInput)

  if (!date || !date.isValid()) { return '—' }

  return date.format('DD-MMM-YYYY')
}

const parseDateInput = (dateInput) => {
  if (dateInput instanceof Date) {
    return parseDateObject(dateInput)
  } else if (typeof dateInput === 'string') {
    return parseDateString(dateInput)
  }
  return null
}

const parseDateObject = (dateObj) => {
  // get the date part of the date object
  const year = dateObj.getFullYear()
  const month = dateObj.getMonth() + 1 // getMonth() returns 0-11
  const day = dateObj.getDate()

  return moment(`${year}-${month}-${day}`, 'YYYY-MM-DD')
}

const parseDateString = (dateString) => {
  // if dateString is in Primevue format. This may not be needed, but leaving
  // it here just in case.
  if (dateString.match(/\bGMT\b/)) {
    const dateComponents = dateString.split(' ')
    const datePart = `${dateComponents[1]} ${dateComponents[2]} ${dateComponents[3]}`
    return moment(datePart, 'MMM DD YYYY')
  }

  // remove time/timezone part of the string if it exists
  const parseString = dateString.includes('T') ? dateString.split('T')[0] : dateString
  let date = moment(parseString, knownDateFormats, true)

  // fallback for unknown formats
  if (!date.isValid()) date = moment(parseString)

  return date
}

export const formatDateToTBPayload = (date) => {
  if (!date) { return '' }
  const requiredFormat = moment(date, knownDateFormats, true).format('YYYY-MM-DD')
  return requiredFormat
}

// Formats a date in the form 'YYYY-MM-DD', e.g. '2023-11-07'.
// Input dateObj format e.g. {day: 29, month: 9, year: 2023, otherMonth: true, today: false, year: 2023}
export const formatDateForHighlight = (dateObj) => {
  if (!dateObj) { return '' }
  const requiredFormat = `${dateObj.year}-${String(dateObj.month + 1).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`
  return requiredFormat
}

export const compareDatesDescending = (a, b) => new Date(b.date) - new Date(a.date)

export const compareDatesAscending = (a, b) => new Date(a.date) - new Date(b.date)

export const getRandomArbitrary = (min, max) => {
  return Math.random() * (max - min) + min
}

export const addDefaultDiagnosis = (diagnosisArr) => {
  const defaultDescription = 'Diagnosis Not Assigned'
  const defaultCode = 'notfound'
  const existingDiagnoses = diagnosisArr || []

  const hasDefault = existingDiagnoses.some(item => item.description === defaultDescription)
  const newArr = hasDefault
    ? existingDiagnoses
    : [{ description: defaultDescription, icd10_code: defaultCode }, ...existingDiagnoses]

  return newArr
}

export const getMonthlyOccurrence = (date) => {
  const dayOfMOnth = date.getDate()
  const occurrence = Math.ceil(dayOfMOnth / 7)

  return occurrence

}

export const getISODay = (date) => {
  const day = date.getDay()
  return (day === 0 ? 6 : day - 1)
}
